import React from "react";
import { useParams } from "react-router-dom";
import { Card, Button, Badge, Avatar } from "../../design-system";
import { Icon, SupSection, AssetMini, StatusBadge, PriorityBadge, CatIcon } from "./atoms";
import { userById, resourceById, ticketsForUser, RES_TYPE, isOpen } from "./data";
import { useSoporteNav } from "./nav";

// Acciones rápidas: icono Lucide (contrato del Button del DS).
const QUICK_ACTIONS = [
  { icon: "Laptop", label: "Asignar equipo" },
  { icon: "Cloud", label: "Asignar recurso IT" },
  { icon: "History", label: "Historial de soporte" },
  { icon: "RefreshCw", label: "Registrar devolución" },
  { icon: "UserMinus", label: "Solicitar baja de accesos" },
  { icon: "FileText", label: "Documentos" },
];

/**
 * Soporte IT · Ficha de usuario — identidad + acciones rápidas, tickets
 * abiertos e históricos y panel lateral con equipos y recursos. UI-only.
 */
function SoporteUsuario({ id }) {
  const nav = useSoporteNav();
  const u = userById(id);
  if (!u) return <div className="kr-page"><div style={{ padding: 32 }}>Usuario no encontrado.</div></div>;
  document.title = `${u.name} · Soporte IT | Kronaly`;
  const tickets = ticketsForUser(u.id);
  const abiertos = tickets.filter((t) => isOpen(t.status));
  const historicos = tickets.filter((t) => !isOpen(t.status));

  return (
    <div className="kr-page">
      <button onClick={() => nav.back()} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "none", background: "transparent", color: "var(--kr-fg-muted)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, alignSelf: "flex-start" }}>
        <Icon name="arrowLeft" size={14} />Volver
      </button>

      {/* header */}
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <Avatar name={u.name} size={64} status={u.status === "activo" ? "working" : "off"} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={{ margin: 0, fontFamily: "var(--kr-font-display)", fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--kr-fg-strong)" }}>{u.name}</h1>
              {u.status === "activo" ? <Badge tone="success" dot>Activo</Badge> : <Badge tone="neutral" dot>Baja</Badge>}
            </div>
            <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", marginTop: 4 }}>{u.role} · {u.dept} · {u.company}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 10, fontSize: 12, color: "var(--kr-fg-muted)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="mail" size={12} />{u.email}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="phone" size={12} />{u.phone}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="mapPin" size={12} />{u.location}</span>
            </div>
          </div>
          <Button variant="primary" icon="Plus" onClick={() => nav.go("crear")}>Crear ticket</Button>
        </div>
        {/* acciones rápidas */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--kr-border-subtle)" }}>
          {QUICK_ACTIONS.map((a, i) => (
            <Button key={i} variant="secondary" size="sm" icon={a.icon}>{a.label}</Button>
          ))}
        </div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 20, alignItems: "start" }} data-responsive-grid="2">
        {/* principal: tickets */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card padded={false}>
            <div style={{ padding: "16px 20px 8px" }}>
              <SupSection eyebrow="Soporte" title={`Tickets abiertos · ${abiertos.length}`} />
            </div>
            <div style={{ padding: "0 20px 8px" }}>
              {abiertos.length ? abiertos.map((t, i) => (
                <TicketLine key={t.code} t={t} onOpen={() => nav.go("ticket", { code: t.code })} first={i === 0} />
              )) : <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", padding: "8px 0 16px" }}>Sin tickets abiertos.</div>}
            </div>
          </Card>

          <Card padded={false}>
            <div style={{ padding: "16px 20px 8px" }}>
              <SupSection eyebrow="Histórico" title={`Tickets resueltos · ${historicos.length}`} />
            </div>
            <div style={{ padding: "0 20px 8px" }}>
              {historicos.length ? historicos.map((t, i) => (
                <TicketLine key={t.code} t={t} onOpen={() => nav.go("ticket", { code: t.code })} first={i === 0} />
              )) : <div style={{ fontSize: 13, color: "var(--kr-fg-muted)", padding: "8px 0 16px" }}>Sin histórico.</div>}
            </div>
          </Card>
        </div>

        {/* lateral: equipos + recursos */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <SupSection eyebrow="Inventario" title={`Equipos asignados · ${u.assets.length}`} />
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {u.assets.length ? u.assets.map((aid) => <AssetMini key={aid} assetId={aid} onOpen={(assetId) => nav.go("activo", { id: assetId })} compact />)
                : <div style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Sin equipos asignados.</div>}
            </div>
          </Card>

          <Card>
            <SupSection eyebrow="Acceso" title={`Recursos y licencias · ${u.resources.length}`} />
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {u.resources.map((rid) => {
                const r = resourceById(rid); const t = RES_TYPE[r.type];
                return (
                  <div key={rid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--kr-border-subtle)", borderRadius: 8 }}>
                    <Icon name={t.icon} size={15} stroke="var(--kr-fg-muted)" />
                    <span style={{ flex: 1, fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>{r.name}</span>
                    <Badge tone="success" variant="soft">Activo</Badge>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card>
            <SupSection eyebrow="Resumen" title="Últimos problemas reportados" />
            <div style={{ marginTop: 12 }}>
              {tickets.slice(0, 3).map((t, i) => (
                <div key={t.code} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: i === 0 ? "none" : "1px solid var(--kr-border-subtle)" }}>
                  <CatIcon cat={t.cat} size={24} />
                  <span style={{ flex: 1, fontSize: 12, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function TicketLine({ t, onOpen, first }) {
  return (
    <div onClick={onOpen} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderTop: first ? "none" : "1px solid var(--kr-border-subtle)", cursor: "pointer" }}>
      <CatIcon cat={t.cat} size={30} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-mono)" }}>{t.code} · {t.updated}</div>
      </div>
      <PriorityBadge priority={t.priority} />
      <StatusBadge status={t.status} />
    </div>
  );
}

// Wrapper de ruta: remonta (key) al cambiar de usuario.
export default function SoporteUsuarioPage() {
  const { id } = useParams();
  return <SoporteUsuario key={id} id={id} />;
}
